import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getCatalog, getZoneImages, mediaUrl, fmt } from "../lib/api";
import { FloorPlan } from "../components/FloorPlan";
import { ZoneMeasures } from "../components/ZoneMeasures";
import { WorkPanel } from "../components/WorkPanel";
import { QuoteDialog } from "../components/QuoteDialog";
import { AddZoneDialog } from "../components/AddZoneDialog";
import { SupportChatPanel } from "../components/SupportChatPanel";
import { SaveProjectDialog, ResumeProjectDialog } from "../components/ProjectDialogs";
import { useProject, storedProject } from "../lib/useProject";
import { patchProject, setProjectContact } from "../lib/projects";
import { Button } from "../components/ui/button";
import {
  emptyInternal,
  emptyExternal,
  zoneSurfaces,
  itemQty,
  UNIT_LABEL,
  VAT_OPTIONS,
} from "../lib/surfaces";
import { Leaf, Settings2, Trash2, Mail, Save, FolderOpen, Check } from "lucide-react";

export default function Configurator() {
  const [catalog, setCatalog] = useState(null);
  const [zones, setZones] = useState([]);
  const [removedPresets, setRemovedPresets] = useState([]);
  const [activeZone, setActiveZone] = useState(null);
  const [config, setConfig] = useState({});
  const [vatRate, setVatRate] = useState(10);
  const [dialog, setDialog] = useState(false);
  const [zoneDialog, setZoneDialog] = useState(false);
  const [zoneImages, setZoneImages] = useState({});

  useEffect(() => {
    getZoneImages()
      .then(setZoneImages)
      .catch(() => setZoneImages({}));
  }, []);

  useEffect(() => {
    getCatalog()
      .then((c) => {
        setCatalog(c);
        const initial = c.rooms.map((r) => ({
          key: r.key,
          name: r.name,
          type: r.type || r.name,
          kind: "internal",
          preset: true,
          dims: emptyInternal(),
        }));
        setZones(initial);
        setActiveZone(initial[0]?.key || null);
      })
      .catch(() => toast.error("Impossibile caricare il catalogo"));
  }, []);

  const zone = zones.find((z) => z.key === activeZone) || zones[0];
  const surfaces = useMemo(() => (zone ? zoneSurfaces(zone) : null), [zone]);

  const addZone = ({ kind, type, name }) => {
    const key = `z-${Date.now()}`;
    setZones((z) => [
      ...z,
      { key, name, type, kind, dims: kind === "internal" ? emptyInternal() : emptyExternal() },
    ]);
    setActiveZone(key);
  };

  const restoreZone = (key) => {
    const preset = removedPresets.find((z) => z.key === key);
    if (!preset) return;
    setRemovedPresets((r) => r.filter((z) => z.key !== key));
    setZones((z) => [...z, preset]);
    setActiveZone(key);
  };

  const removeZone = (key) => {
    const target = zones.find((z) => z.key === key);
    if (target?.preset) setRemovedPresets((r) => [...r, target]);
    setZones((z) => z.filter((x) => x.key !== key));
    setConfig((c) => {
      const next = { ...c };
      delete next[key];
      return next;
    });
    if (activeZone === key) {
      const rest = zones.filter((z) => z.key !== key);
      setActiveZone(rest[0]?.key || null);
    }
  };

  const updateDims = (dims) =>
    setZones((z) => z.map((x) => (x.key === zone.key ? { ...x, dims } : x)));

  const renameZone = (name) =>
    setZones((z) => z.map((x) => (x.key === zone.key ? { ...x, name } : x)));

  const toggle = (item) =>
    setConfig((c) => {
      const cur = { ...(c[zone.key] || {}) };
      if (cur[item.id]) delete cur[item.id];
      else if (item.surface_basis === "walls")
        cur[item.id] = { wallMode: "all", wallHeight: surfaces.height };
      else if (item.surface_basis === "none") cur[item.id] = { qty: item.default_qty || 1 };
      else cur[item.id] = {};
      return { ...c, [zone.key]: cur };
    });

  const setItemCfg = (itemId, patch) =>
    setConfig((c) => ({
      ...c,
      [zone.key]: { ...(c[zone.key] || {}), [itemId]: { ...(c[zone.key]?.[itemId] || {}), ...patch } },
    }));

  const { lines, netTotal, vatAmount, total, counts, areaByZone } = useMemo(() => {
    if (!catalog) return { lines: [], netTotal: 0, vatAmount: 0, total: 0, counts: {}, areaByZone: {} };
    const byId = Object.fromEntries(catalog.items.map((i) => [i.id, i]));
    const out = [];
    const counts = {};
    const areaByZone = {};
    zones.forEach((z) => {
      const surf = zoneSurfaces(z);
      areaByZone[z.key] = z.kind === "external" ? surf.area : surf.floor;
      const sel = config[z.key] || {};
      const keys = Object.keys(sel);
      if (keys.length) counts[z.key] = keys.length;
      keys.forEach((id) => {
        const it = byId[id];
        if (!it) return;
        const qty = itemQty(it, surf, sel[id]);
        out.push({
          room_key: z.key,
          room_name: z.name,
          item_id: it.id,
          item_name: it.name,
          unit: it.unit,
          qty: Math.round(qty * 100) / 100,
          unit_price: it.price,
          total: Math.round(qty * it.price * 100) / 100,
        });
      });
    });
    const net = out.reduce((s, l) => s + l.total, 0);
    const vat = (net * vatRate) / 100;
    return { lines: out, netTotal: net, vatAmount: vat, total: net + vat, counts, areaByZone };
  }, [catalog, config, zones, vatRate]);

  // ---- progetto persistente: stato serializzato, snapshot per l'Admin ----
  const projectState = useMemo(
    () => ({ zones, removedPresets, config, vatRate, activeZone, lines }),
    [zones, removedPresets, config, vatRate, activeZone, lines]
  );

  const snapshot = useMemo(() => {
    const measured = zones.filter((z) => zoneSurfaces(z).area > 0 || zoneSurfaces(z).floor > 0);
    const totalArea =
      Math.round(zones.reduce((s, z) => s + (areaByZone[z.key] || 0), 0) * 10) / 10;
    const phase = !zones.length
      ? "Configurazione ambienti"
      : lines.length
        ? "Scelta lavorazioni"
        : measured.length
          ? "Inserimento misure"
          : "Configurazione ambienti";
    const single = zones.length === 1 ? zones[0] : null;
    const title = !zones.length
      ? ""
      : single
        ? `${single.kind === "external" ? single.type : `Ristrutturazione ${single.type.toLowerCase()}`} – ${totalArea} m²`
        : `${zones.length} ambienti – ${totalArea} m²`;
    return {
      title,
      phase,
      section: zone ? zone.name : "",
      zones: zones.map((z) => z.name),
      zone_count: zones.length,
      item_count: lines.length,
      area: totalArea,
      net: Math.round(netTotal * 100) / 100,
      vat_rate: vatRate,
      vat: Math.round(vatAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  }, [zones, lines, areaByZone, zone, netTotal, vatAmount, total, vatRate]);

  const restore = React.useCallback((s) => {
    if (s.zones) setZones(s.zones);
    if (s.removedPresets) setRemovedPresets(s.removedPresets);
    if (s.config) setConfig(s.config);
    if (s.vatRate) setVatRate(s.vatRate);
    if (s.activeZone) setActiveZone(s.activeZone);
  }, []);

  const { project, savedAt, dirty, bioedilOnline, unread, setUnread, save, resume } = useProject({
    state: projectState,
    snapshot,
    ready: !!catalog,
    onRestore: restore,
  });

  const [saveDialog, setSaveDialog] = useState(false);
  const [resumeDialog, setResumeDialog] = useState(false);
  const [projectLink, setProjectLink] = useState("");
  const hasStored = !!storedProject();

  const doSave = async () => {
    try {
      const res = await save();
      if (!res) return;
      setProjectLink(`${window.location.origin}${res.path}`);
      setSaveDialog(true);
    } catch {
      toast.error("Salvataggio non riuscito, riprova");
    }
  };

  if (!catalog) {
    return (
      <div className="flex h-screen items-center justify-center label-xs text-muted-foreground">
        Caricamento cantiere…
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-border bg-[#1B2A20] text-[#F4F7F2]">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3">
            <Leaf className="h-5 w-5 text-[#8FBF6F]" />
            <span className="font-display text-base font-bold uppercase tracking-[0.2em]">
              BioEdil <span className="text-[#8FBF6F]">Abruzzo</span>
            </span>
          </Link>
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="label-xs flex items-center gap-2 text-[#9BB0A2] transition-colors hover:text-[#8FBF6F]"
          >
            <Settings2 className="h-3.5 w-3.5" /> Admin
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8 lg:py-14">
        <div className="rise max-w-2xl">
          <div className="label-xs text-[#4E7C59]">Bioedilizia e ristrutturazioni in Abruzzo</div>
          <h1 className="mt-3 font-display text-3xl font-bold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Configura il tuo intervento
          </h1>
          <p className="mt-4 text-sm text-muted-foreground sm:text-base">
            Aggiungi ambiente o area → inserisci le misure → il calcolo è automatico → scegli le
            lavorazioni → totale. Prezzi netti, IVA esclusa.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              data-testid="btn-save-project"
              onClick={doSave}
              disabled={!project}
              className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
            >
              <Save className="mr-2 h-4 w-4" /> Salva progetto
            </Button>
            <Button
              data-testid="btn-resume-project"
              variant="outline"
              onClick={() => setResumeDialog(true)}
              className="rounded-none"
            >
              <FolderOpen className="mr-2 h-4 w-4" /> Riprendi progetto
            </Button>
            <span data-testid="autosave-indicator" className="label-xs text-muted-foreground">
              {!project ? (
                "Sessione in avvio…"
              ) : dirty ? (
                "Salvataggio in corso…"
              ) : savedAt ? (
                <span className="inline-flex items-center gap-1">
                  <Check className="h-3 w-3 text-[#4E7C59]" /> Ultimo salvataggio{" "}
                  {new Date(savedAt).toLocaleTimeString("it-IT", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              ) : (
                "Salvataggio automatico attivo"
              )}
            </span>
            {project?.saved && (
              <span data-testid="project-code-badge" className="font-mono-t text-xs text-[#4E7C59]">
                {project.code}
              </span>
            )}
          </div>
          {hasStored && !project?.saved && (
            <p className="mt-2 label-xs text-muted-foreground">
              Hai già iniziato un progetto? Usa “Riprendi progetto” con il tuo link personale.
            </p>
          )}
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_380px]">
          <div className="space-y-8">
            <FloorPlan
              zones={zones}
              activeZone={zone?.key}
              onSelect={setActiveZone}
              onRemove={removeZone}
              counts={counts}
              areaByZone={areaByZone}
              imageByZone={Object.fromEntries(
                zones
                  .map((z) => [z.key, zoneImages[z.imageOverrideType || z.type]])
                  .filter(([, url]) => !!url)
                  .map(([k, url]) => [k, mediaUrl(url)])
              )}
              onAdd={() => setZoneDialog(true)}
            />

            {zone ? (
              <>
                <ZoneMeasures
                  zone={zone}
                  verticalTypes={catalog.vertical_types}
                  zoneImages={zoneImages}
                  onChange={updateDims}
                  onRename={renameZone}
                  onImageOverride={(type) =>
                    setZones((z) =>
                      z.map((x) => (x.key === zone.key ? { ...x, imageOverrideType: type } : x))
                    )
                  }
                />
                <SupportChatPanel
                  project={project}
                  bioedilOnline={bioedilOnline}
                  unread={unread}
                  onRead={() => setUnread(0)}
                />
                <WorkPanel
                  categories={catalog.categories}
                  items={catalog.items}
                  zone={zone}
                  surfaces={surfaces}
                  selection={config[zone.key] || {}}
                  onToggle={toggle}
                  onCfg={setItemCfg}
                />
              </>
            ) : (
              <div className="border border-dashed border-border bg-card px-6 py-12 text-center">
                <p className="text-sm text-muted-foreground">
                  Nessun ambiente nel progetto. Aggiungi un ambiente interno o un'area esterna per
                  iniziare.
                </p>
                <Button
                  data-testid="btn-add-zone-empty"
                  onClick={() => setZoneDialog(true)}
                  className="mt-4 rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
                >
                  Aggiungi ambiente o area
                </Button>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-8 lg:self-start">
            <div className="border-2 border-[#1B2A20] bg-card">
              <div className="bg-[#1B2A20] px-5 py-4 text-[#F4F7F2]">
                <div className="label-xs text-[#9BB0A2]">Totale stimato IVA inclusa</div>
                <div
                  data-testid="live-total-price-display"
                  className="font-mono-t text-3xl font-bold tracking-tighter sm:text-4xl"
                >
                  € {fmt(total)}
                </div>
                <div className="label-xs mt-1 text-[#9BB0A2]">
                  netto € {fmt(netTotal)} + IVA {vatRate}%
                </div>
              </div>

              <div className="max-h-[300px] overflow-y-auto px-5 py-4" data-testid="summary-list">
                {lines.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Nessuna lavorazione selezionata.
                  </p>
                ) : (
                  zones.map((z) => {
                    const rl = lines.filter((l) => l.room_key === z.key);
                    if (!rl.length) return null;
                    return (
                      <div key={z.key} className="mb-4">
                        <div className="label-xs mb-2 border-b border-border pb-1 text-[#4E7C59]">
                          {z.name}
                        </div>
                        {rl.map((l) => (
                          <div key={l.item_id} className="flex justify-between gap-3 py-1 text-xs">
                            <span className="truncate">
                              {l.item_name}
                              <span className="ml-1 font-mono-t text-muted-foreground">
                                {l.qty.toLocaleString("it-IT", { maximumFractionDigits: 2 })}{" "}
                                {UNIT_LABEL[l.unit] || l.unit} × € {fmt(l.unit_price)}
                              </span>
                            </span>
                            <span className="font-mono-t whitespace-nowrap">€ {fmt(l.total)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="space-y-2 border-t border-border px-5 py-4 text-sm">
                <div className="flex justify-between font-semibold">
                  <span>Totale lavori netto</span>
                  <span className="font-mono-t" data-testid="summary-net-total">
                    € {fmt(netTotal)}
                  </span>
                </div>
                <label className="block pt-1">
                  <span className="label-xs text-muted-foreground">
                    Seleziona l'IVA da applicare alla simulazione
                  </span>
                  <select
                    data-testid="vat-selector"
                    value={vatRate}
                    onChange={(e) => setVatRate(Number(e.target.value))}
                    className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[#4E7C59]"
                  >
                    {VAT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Importo IVA</span>
                  <span className="font-mono-t" data-testid="summary-vat-amount">
                    € {fmt(vatAmount)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 text-base font-bold">
                  <span>Totale stimato</span>
                  <span className="font-mono-t text-[#4E7C59]">€ {fmt(total)}</span>
                </div>
              </div>

              <div className="space-y-2 border-t border-border p-5">
                <Button
                  data-testid="btn-open-quote-dialog"
                  disabled={!lines.length}
                  onClick={() => setDialog(true)}
                  className="w-full rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
                >
                  <Mail className="mr-2 h-4 w-4" /> Ricevi preventivo via email
                </Button>
                <Button
                  data-testid="btn-reset-config"
                  variant="ghost"
                  disabled={!lines.length}
                  onClick={() => setConfig({})}
                  className="w-full rounded-none label-xs"
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" /> Azzera configurazione
                </Button>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Tutti i prezzi sono netti, IVA esclusa. L'aliquota selezionata viene utilizzata
              esclusivamente per questa simulazione. L'aliquota IVA effettivamente applicabile sarà
              verificata da BioEdil Abruzzo in base alla tipologia dell'intervento, dell'immobile e
              ai requisiti fiscali previsti.
            </p>
          </aside>
        </div>
      </main>

      <QuoteDialog
        open={dialog}
        onOpenChange={setDialog}
        lines={lines}
        netTotal={netTotal}
        vatRate={vatRate}
        vatAmount={vatAmount}
        total={total}
        projectId={project?.id || null}
        onSent={(contact) => {
          if (!project) return;
          patchProject(project.id, project.token, { status: "completato" }).catch(() => {});
          setProjectContact(project.id, project.token, contact).catch(() => {});
        }}
      />
      <AddZoneDialog
        open={zoneDialog}
        onOpenChange={setZoneDialog}
        internalTypes={catalog.internal_types}
        externalTypes={catalog.external_types}
        hiddenPresets={removedPresets}
        onRestore={restoreZone}
        onCreate={addZone}
      />
      <SaveProjectDialog
        open={saveDialog}
        onOpenChange={setSaveDialog}
        project={project}
        link={projectLink}
      />
      <ResumeProjectDialog
        open={resumeDialog}
        onOpenChange={setResumeDialog}
        onResume={resume}
      />
    </div>
  );
}
